import React, { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import ChevronIcon from '~/icons/chevron-down.svg';
import { breakpoints } from '~/theme/breakpoints';

interface AccordionProps {
    title: string;
    isOpen?: boolean;
    initOpenForSEO?: boolean;
    children: React.ReactNode;
    onToggle?: (isOpen: boolean, accordion: string) => void;
}

interface AccordionContainerProps {
    contentHeight?: number;
    isOpen?: boolean;
}

const StyledContainer = styled.div(() => ({
    overflow: 'hidden',
}));

const StyledAccordionContainer = styled.div<AccordionContainerProps>(
    ({ isOpen, contentHeight }) => ({
        transition: 'max-height 0.3s ease',
        overflow: 'hidden',
        maxHeight: isOpen ? contentHeight : 0,
        '*': {
            visibility: isOpen ? 'visible' : 'hidden',
        },
    }),
);

const StyledContent = styled.div(({ theme }) => ({
    padding: `0 ${theme.space[3]} ${theme.space[3]} ${theme.space[3]}`,
}));

export const StyledAccordionTrigger = styled.button(({ theme }) => ({
    display: 'flex',
    gap: theme.space[4],
    gridTemplateColumns: '1fr min-content',
    alignItems: 'center',
    justifyContent: 'space-between',

    // Button reset
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    cursor: 'pointer',
    textAlign: 'left',
    color: theme.colors.white,
    backfaceVisibility: 'hidden',
    webkitTapHighlightColor: 'transparent',

    ':focus-visible': {
        boxShadow: theme.shadows.accessibilityInset,
    },
}));

export const Chevron = styled.div<{ isOpen?: boolean }>(({ isOpen, theme }) => ({
    color: theme.colors.black,
    transform: `rotateX(${isOpen ? '180deg' : '0deg'})`,
    transition: 'transform 175ms ease-out',
}));

export const StyledAccordionTitle = styled.h4(({ theme }) => ({
    ...theme.mixins.useTextStyle('sectionLabel'),
    width: '100%',
    color: theme.colors.black,
    textAlign: 'center',
    textTransform: 'uppercase',
    padding: `${theme.space[3]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    [breakpoints.sm]: {
        padding: `${theme.space[3]}`,
        borderTop: `1px solid ${theme.colors.brownLight}`,
    },
}));

const Accordion: React.FC<AccordionProps> = ({
    onToggle,
    title,
    children,
    isOpen,
    initOpenForSEO,
}) => {
    const [isOpenAccordion, setIsOpenAccordion] = useState(isOpen);
    const contentRef = useRef<HTMLDivElement>(null);
    const contentHeight = contentRef.current?.scrollHeight ?? 0;
    const handleToggle = () => {
        onToggle && onToggle(!isOpenAccordion, title);
        setIsOpenAccordion(!isOpenAccordion);
    };

    useEffect(() => {
        if (isOpen || initOpenForSEO) {
            setIsOpenAccordion(true);
        }
    }, [isOpen, initOpenForSEO]);

    useEffect(() => {
        if (initOpenForSEO) {
            // close immediately after component render
            setIsOpenAccordion(false);
        }
    }, []);

    return (
        <StyledContainer>
            <StyledAccordionTrigger aria-expanded={isOpenAccordion} onClick={handleToggle}>
                <StyledAccordionTitle>
                    {title}
                    <Chevron isOpen={isOpenAccordion}>
                        <ChevronIcon />
                    </Chevron>
                </StyledAccordionTitle>
            </StyledAccordionTrigger>

            <StyledAccordionContainer
                role="contentinfo"
                aria-labelledby={title}
                isOpen={isOpenAccordion}
                contentHeight={contentHeight}
            >
                <StyledContent ref={contentRef}>{children}</StyledContent>
            </StyledAccordionContainer>
        </StyledContainer>
    );
};

export default Accordion;
